/**
 * Route-by-kind — the single weiche that decides which editor a file opens in
 * (CC_05 Block 1, D-29). Both the file list (a click on a row) and a scene/
 * element link (a click on a link) must reach the SAME decision, because the
 * v1.3.0 data-loss bug was exactly a link that always opened the board editor:
 * clicking a link onto a `kind='md'` note opened the board, whose autosave then
 * overwrote the markdown blob on the shared S3 key.
 *
 * `files.kind` stays an open discriminator (CLAUDE.md §5). The weiche maps the
 * kinds that HAVE an editor to their route and returns `null` for everything
 * else — so an unknown kind never falls back to the board editor (Audit #20,
 * finalised in Block 8). The server-side write-barrier (`expectKind`) is the
 * second line of defence: even a future mis-route can no longer overwrite.
 */

import { ryfeApi } from "./api";
import { navigate } from "./router";

/**
 * The in-app route for a file of the given kind, or `null` when the kind has no
 * editor (e.g. `image`, or any kind we don't know yet). Callers MUST treat null
 * as "do not open" — never as "open the board".
 */
export const routeForKind = (
  kind: string | null | undefined,
  fileId: string,
): string | null => {
  switch (kind) {
    case "board":
      return `/board/${fileId}`;
    case "md":
      return `/md/${fileId}`;
    case "list":
      return `/list/${fileId}`;
    default:
      return null;
  }
};

/**
 * Resolve a file's kind across the given workspace candidates and navigate to
 * the matching editor. Used by the scene/element link path (Block 1/6): the link
 * only stores the target file id, so the kind must be fetched before routing.
 *
 * Returns the outcome so a caller can surface "no editor for this type" instead
 * of silently doing nothing. Never opens the board for an unknown kind.
 */
export async function openFileById(
  wsCandidates: string[],
  fileId: string,
): Promise<"opened" | "no-editor" | "not-found"> {
  const seen = new Set<string>();
  for (const ws of wsCandidates) {
    if (!ws || seen.has(ws)) {
      continue;
    }
    seen.add(ws);
    try {
      const meta = await ryfeApi.getFile(ws, fileId);
      const route = routeForKind(meta.kind, fileId);
      if (route) {
        navigate(route);
        return "opened";
      }
      return "no-editor";
    } catch {
      /* try the next workspace; the file may live in a shared tenant */
    }
  }
  return "not-found";
}
